import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { Building2, Users, Mail, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { fetchWithAuth } from '../utils/api';
import { API_URL } from '../utils/apiConfig';

const JoinWorkspacePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [workspaceOwnership, setWorkspaceOwnership] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link - no token provided');
      setLoading(false);
      return;
    }

    fetchInvitationDetails();
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && invitation) {
      checkWorkspaceOwnership();
    }
  }, [isAuthenticated, invitation]);

  const fetchInvitationDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/workspace/invitation/${token}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch invitation details');
      }
      
      const data = await response.json();
      setInvitation(data.invitation);
      setError(null);
    } catch (err) {
      console.error('Error fetching invitation:', err);
      setError(
        err.message || 
        'Failed to load invitation details. The link may be expired or invalid.'
      );
    } finally {
      setLoading(false);
    }
  };

  const checkWorkspaceOwnership = async () => {
    try {
      const data = await fetchWithAuth(`/workspace/check-ownership/${token}`);
      setWorkspaceOwnership(data);
    } catch (err) {
      console.error('Error checking workspace ownership:', err);
      // If check fails, we'll proceed without the ownership info
      setWorkspaceOwnership(null);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      const returnUrl = `/join-workspace?token=${token}`;
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }

    // Check if user owns a workspace and show confirmation if needed
    if (workspaceOwnership && (workspaceOwnership.ownsWorkspace || workspaceOwnership.hasExistingMembership)) {
      // If they're already a member of the target workspace, proceed directly
      if (workspaceOwnership.alreadyMemberOfTarget) {
        await proceedWithJoin();
        return;
      }
      
      setShowConfirmation(true);
      return;
    }

    await proceedWithJoin();
  };

  const proceedWithJoin = async () => {
    try {
      setJoining(true);
      setShowConfirmation(false);
      
      const data = await fetchWithAuth('/workspace/accept-invitation', {
        method: 'POST',
        body: JSON.stringify({ token })
      });
      
      // Show appropriate success message based on whether workspace was switched
      if (data.workspaceSwitched && data.previousWorkspace) {
        const workspaceType = data.previousWorkspace.type === 'owned' ? 'workspace' : 'workspace';
        const actionText = data.previousWorkspace.type === 'owned' ? 'owned' : 'were a member of';
        
        toast.success(
          `Successfully joined ${data.workspace.name}! The ${workspaceType} "${data.previousWorkspace.name}" you ${actionText} is still available and you can return to it anytime by leaving this workspace.`,
          { duration: 7000 }
        );
      } else {
        toast.success(data.message || 'Successfully joined workspace!');
      }
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Error accepting invitation:', err);
      const errorMessage = err.message || 'Failed to join workspace';
      
      if (err.message.includes('invitation is for')) {
        toast.error(err.message);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setJoining(false);
    }
  };

  const handleSignUp = () => {
    const returnUrl = `/join-workspace?token=${token}`;
    navigate(`/signup?returnUrl=${encodeURIComponent(returnUrl)}&email=${encodeURIComponent(invitation?.email || '')}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Invalid Invitation</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        
        {/* Header */}
        <div className="bg-blue-600 text-white p-8 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Workspace Invitation</h1>
          <p className="opacity-90">You've been invited to join a team</p>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="space-y-6">
            
            {/* Invitation Details */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2 text-blue-600" />
                Workspace Details
              </h2>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-500">Workspace</label>
                  <p className="font-medium text-gray-900">{invitation?.workspace?.name}</p>
                </div>
                
                <div>
                  <label className="text-sm text-gray-500">Invited by</label>
                  <p className="font-medium text-gray-900">{invitation?.invitedBy?.name}</p>
                  <p className="text-sm text-gray-600">{invitation?.invitedBy?.email}</p>
                </div>
                
                <div>
                  <label className="text-sm text-gray-500">Your role</label>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                    {invitation?.role}
                  </span>
                </div>
                
                <div>
                  <label className="text-sm text-gray-500 flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    Invitation expires
                  </label>
                  <p className="text-sm text-gray-600">
                    {new Date(invitation?.expiresAt).toLocaleDateString()} at {new Date(invitation?.expiresAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Email Verification */}
            {invitation?.email && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">Email Verification Required</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      This invitation is for <strong>{invitation.email}</strong>. 
                      {isAuthenticated && user?.email !== invitation.email && (
                        <span className="block mt-1">
                          You're currently logged in as <strong>{user?.email}</strong>. 
                          Please log in with the correct email address.
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {!isAuthenticated ? (
                <>
                  <button
                    onClick={handleSignUp}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Create Account & Join Workspace
                  </button>
                  <button
                    onClick={() => {
                      const returnUrl = `/join-workspace?token=${token}`;
                      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
                    }}
                    className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    I Already Have an Account
                  </button>
                </>
              ) : user?.email === invitation?.email ? (
                <button
                  onClick={handleAcceptInvitation}
                  disabled={joining}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {joining ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Joining...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Accept Invitation
                    </>
                  )}
                </button>
              ) : (
                <div className="text-center space-y-3">
                  <p className="text-sm text-red-600">
                    You're logged in with a different email address. Please log out and sign in with {invitation?.email} to accept this invitation.
                  </p>
                  <button
                    onClick={() => navigate('/logout')}
                    className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Log Out & Sign In with Correct Email
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="text-center">
              <p className="text-xs text-gray-500">
                By joining this workspace, you agree to collaborate on documents and signatures within the team.
              </p>
            </div>

          </div>
        </div>
      </div>

      {/* Workspace Switch Confirmation Dialog */}
      {showConfirmation && workspaceOwnership && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Switch Active Workspace?
              </h3>
              <p className="text-gray-600">
                You're about to join <strong>{workspaceOwnership.targetWorkspace.name}</strong> as your active workspace.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-blue-900 mb-2">What this means:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>{workspaceOwnership.targetWorkspace.name}</strong> will become your active workspace</li>
                {workspaceOwnership.ownsWorkspace && (
                  <li>• Your workspace <strong>"{workspaceOwnership.ownedWorkspace.name}"</strong> will remain intact</li>
                )}
                {workspaceOwnership.hasExistingMembership && (
                  <li>• Your current workspace <strong>"{workspaceOwnership.existingWorkspace.name}"</strong> will remain available</li>
                )}
                <li>• You can return to your previous workspace anytime by leaving this one</li>
                <li>• All your documents and data will be preserved</li>
              </ul>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={proceedWithJoin}
                disabled={joining}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {joining ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Joining...
                  </>
                ) : (
                  'Yes, Switch Workspace'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JoinWorkspacePage;